import { useCallback, useEffect, useRef, useState } from 'react';
import type { InterestGroup, InterestOutcome, Match } from '@sfc/sdk';
import { PlatformApiError } from '@sfc/sdk';
import { platform } from '../api/client';

export type InboxState =
  | { status: 'loading' }
  | { status: 'unreachable' }
  | { status: 'ready'; ideas: InterestGroup[]; waiting: number; problem?: string };

/**
 * Who is waiting on your ideas, and answering them.
 *
 * Lifted above the screen that draws it because the count is also a badge on
 * the navigation: two copies fetched separately would disagree the moment
 * somebody accepted anybody.
 */
export function useInterest(enabled: boolean): {
  state: InboxState;
  answer: (interestId: string, outcome: InterestOutcome) => Promise<Match | undefined>;
  refresh: () => void;
} {
  const [state, setState] = useState<InboxState>({ status: 'loading' });
  const live = useRef(true);

  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    try {
      const inbox = await platform.match.interest();
      if (live.current) setState({ status: 'ready', ideas: inbox.ideas, waiting: inbox.waiting });
    } catch {
      if (live.current) setState({ status: 'unreachable' });
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    void load();

    /*
     * Asked again when the app comes back to the front.
     *
     * Interest arrives while nobody is looking, and a count fetched once when
     * the page loaded is wrong by the time anybody reads it -- which makes the
     * badge worse than no badge, because it says nobody is waiting when
     * somebody is. Polling on a timer would ask all day for an answer that
     * changes a few times a week; coming back to the app is the moment the
     * answer matters.
     */
    const again = () => {
      if (document.visibilityState === 'visible') void load();
    };
    window.addEventListener('focus', again);
    document.addEventListener('visibilitychange', again);
    return () => {
      window.removeEventListener('focus', again);
      document.removeEventListener('visibilitychange', again);
    };
  }, [enabled, load]);

  /**
   * Answers one, and takes it off the screen either way.
   *
   * Removed before the server replies, like a swipe, because the answer is a
   * decision somebody has made and watching it sit there is worse than being
   * briefly optimistic. A refusal -- already answered, by another tab or
   * another device -- reloads the inbox and says so rather than putting the
   * card back as if nothing happened.
   */
  const answer = useCallback(
    async (interestId: string, outcome: InterestOutcome): Promise<Match | undefined> => {
      setState((current) => (current.status === 'ready' ? without(current, interestId) : current));

      try {
        const decision = await platform.match.decide(interestId, outcome);
        void load();
        return decision.match;
      } catch (error) {
        const stale = error instanceof PlatformApiError;
        await load();
        if (stale && live.current) {
          setState((current) =>
            current.status === 'ready'
              ? { ...current, problem: 'That one had already been answered.' }
              : current,
          );
        }
        return undefined;
      }
    },
    [load],
  );

  return { state, answer, refresh: load };
}

function without(current: Extract<InboxState, { status: 'ready' }>, interestId: string): InboxState {
  const ideas = current.ideas
    .map((idea) => ({ ...idea, pending: idea.pending.filter((one) => one.id !== interestId) }))
    // An idea nobody is waiting on is not a heading with nothing under it.
    .filter((idea) => idea.pending.length > 0);

  const { problem: _cleared, ...rest } = current;
  return { ...rest, ideas, waiting: Math.max(current.waiting - 1, 0) };
}
